import React, { useState, useEffect } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import Newsletter from './newsletter-signup';
import { GatsbyImage } from 'gatsby-plugin-image';

const DISMISS_KEY = 'newsletterDismissed';
const MINIMISE_KEY = 'newsletterMinimised';
const SIGNUP_SUCCESS_KEY = 'newsletterSignupSuccess';

const NewsletterModal = () => {
	const { newsletterModal } = useStaticQuery(graphql`
		{
			newsletterModal: datoCmsNewsletterPopup {
				title
				copy
				minimisedTitle
				image {
					gatsbyImageData(
						width: 1000
						placeholder: BLURRED
						imgixParams: { auto: "format,compress" }
					)
					alt
				}
				mobileImage {
					gatsbyImageData(
						width: 1000
						placeholder: BLURRED
						imgixParams: { auto: "format,compress" }
					)
					alt
				}
				emailLabel
				nameLabel
				privacyPolicyText
				buttonText
				time
				minimised
				tagId
				tagName
			}
		}
	`);

	const {
		title,
		copy,
		image,
		mobileImage,
		emailLabel,
		nameLabel,
		privacyPolicyText,
		buttonText,
		time,
		minimised: allowMinimised,
		minimisedTitle,
		tagId,
		tagName,
	} = newsletterModal;

	const [showModal, setShowModal] = useState(false);
	const [showMinimised, setShowMinimised] = useState(false);
	const [hasSignedUp, setHasSignedUp] = useState(false);

	useEffect(() => {
		const dismissed = localStorage.getItem(DISMISS_KEY);
		const wasMinimised = localStorage.getItem(MINIMISE_KEY);
		const alreadySignedUp = localStorage.getItem(SIGNUP_SUCCESS_KEY);

		// User already subscribed → never show anything again
		if (alreadySignedUp) return;

		// User dismissed the popup before → do not show again
		if (dismissed) return;

		// Show minimised version if allowed
		if (wasMinimised && allowMinimised) {
			setShowMinimised(true);
			return;
		}

		// Otherwise show full popup after delay
		const delay = setTimeout(() => {
			setShowModal(true);
		}, (time || 5) * 1000);

		return () => clearTimeout(delay);
	}, [allowMinimised, time]);

	// CLOSE FULL MODAL
	const dismissModal = () => {
		setShowModal(false);

		if (hasSignedUp) {
			// User completed signup → hide permanently
			localStorage.setItem(SIGNUP_SUCCESS_KEY, 'true');
			setShowMinimised(false);
			return;
		}

		localStorage.setItem(DISMISS_KEY, 'true');

		if (allowMinimised) {
			localStorage.setItem(MINIMISE_KEY, 'true');
			setShowMinimised(true);
		}
	};

	// OPEN FROM MINIMISED
	const openFromMinimised = () => {
		setShowMinimised(false);
		setShowModal(true);
	};

	// When newsletter signup succeeds
	const handleSuccess = () => {
		setHasSignedUp(true);
	};

	if (!newsletterModal) return null;

	return (
		<>
			{/* Minimized Bubble */}
			{showMinimised && (
				<button
					className='newsletter-minimised background-dark-green rounded-edges backdrop'
					onClick={openFromMinimised}
					aria-label='Open newsletter signup'
				>
					{minimisedTitle}
				</button>
			)}

			{/* Full Modal */}
			{showModal && (
				<div
					className='overlay newsletter-modal-overlay is-active'
					role='dialog'
				>
					<div className='newsletter-modal background-texture backdrop rounded-edges'>
						<div className='close-btn' onClick={dismissModal} />

						<div className='image-container'>
							<GatsbyImage
								image={image.gatsbyImageData}
								alt={image.alt}
								className='desktop-image'
							/>
							{mobileImage && (
								<GatsbyImage
									image={mobileImage.gatsbyImageData}
									alt={mobileImage.alt}
									className='mobile-image'
								/>
							)}
						</div>

						<div class='newsletter-modal-content'>
							<h3>{title}</h3>
							<p>{copy}</p>

							<Newsletter
								button={buttonText}
								placeholder={emailLabel}
								smallprint={privacyPolicyText}
								nameBoolean={!!nameLabel}
								namePlaceholder={nameLabel}
								tagId={tagId}
								tagName={tagName}
								onSuccess={handleSuccess}
							/>
						</div>
					</div>
				</div>
			)}
		</>
	);
};

export default NewsletterModal;
